import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)
let animInstanceId = 0
function splitTextToChars(element) {
  if (!element) return []
  const chars = []
  function processTextNodes(node) {
    const walker = document.createTreeWalker(
      node,
      NodeFilter.SHOW_TEXT,
      null,
      false
    )
    const textNodes = []
    let textNode = walker.nextNode()
    while (textNode) {
      if (textNode.textContent.trim()) {
        textNodes.push(textNode)
      }
      textNode = walker.nextNode()
    }
    textNodes.forEach(textNode => {
      const parent = textNode.parentNode
      const text = textNode.textContent
      const fragment = document.createDocumentFragment()
      text.split('').forEach(char => {
        const span = document.createElement('span')
        span.className = 'split-char'
        span.style.display = 'inline-block'
        span.textContent = char === ' ' ? '\u00A0' : char
        fragment.appendChild(span)
        chars.push(span)
      })
      parent.replaceChild(fragment, textNode)
    })
  }
  processTextNodes(element)
  return chars
}
export default function Anim({ locationTagRef }) {
  const instanceIdRef = useRef(null)
  const triggerRef = useRef(null)
  const fadeInTriggerRef = useRef(null)
  const skillsTriggerRef = useRef(null)
  const skillsScrollTriggersRef = useRef([])
  const cosmicTriggerRef = useRef(null)
  const timeoutsRef = useRef([])
  const gsapAnimationsRef = useRef([])
  const isCleanedUpRef = useRef(false)
  useEffect(() => {
    if (instanceIdRef.current === null) {
      instanceIdRef.current = ++animInstanceId
    }
    isCleanedUpRef.current = false
    const locationTag = locationTagRef?.current
    const section2 = locationTag?.closest('.section-2')
    const cleanup = () => {
      if (isCleanedUpRef.current) return
      isCleanedUpRef.current = true
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout))
      timeoutsRef.current = []
      gsapAnimationsRef.current.forEach(anim => {
        if (anim && anim.kill) anim.kill()
      })
      gsapAnimationsRef.current = []
      if (triggerRef.current) {
        triggerRef.current.kill()
        triggerRef.current = null
      }
      if (fadeInTriggerRef.current) {
        fadeInTriggerRef.current.kill()
        fadeInTriggerRef.current = null
      }
      if (skillsTriggerRef.current) {
        skillsTriggerRef.current.kill()
        skillsTriggerRef.current = null
      }
      skillsScrollTriggersRef.current.forEach(trigger => {
        if (trigger && trigger.kill) trigger.kill()
      })
      skillsScrollTriggersRef.current = []
      if (cosmicTriggerRef.current) {
        cosmicTriggerRef.current.kill()
        cosmicTriggerRef.current = null
      }
    }
    if (locationTag && section2) {
      const chars = splitTextToChars(locationTag)
      if (chars.length > 0) {
        gsap.set(chars, { y: 80, opacity: 0 })
        const fadeInAnim = gsap.to(chars, {
          scrollTrigger: {
            trigger: locationTag,
            start: 'center center',
            toggleActions: 'play none none reverse',
          },
          duration: 0.46,
          ease: 'circ.out',
          y: 0,
          opacity: 1,
          stagger: 0.015,
        })
        fadeInTriggerRef.current = fadeInAnim.scrollTrigger
        gsapAnimationsRef.current.push(fadeInAnim)
        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: section2,
            start: 'top top',
            end: () => `+=${chars.length * 50}`,
            pin: true,
            scrub: 1,
          }
        })
        chars.reverse().forEach((char, index) => {
          timeline.to(char, {
            opacity: 0,
            y: -50,
            duration: 0.1,
            ease: 'power2.in',
          }, index * 0.05)
        })
        triggerRef.current = timeline.scrollTrigger
        gsapAnimationsRef.current.push(timeline)
      }
    }
    const skillsTimeout = setTimeout(() => {
      if (isCleanedUpRef.current) return
      const section4 = document.querySelector('.section-4')
      const section4Title = document.querySelector('.section-4-title')
      const skillsGrid = document.querySelector('.skills-grid')
      const skillsGroupsWrapper = document.querySelector('.skills-groups-wrapper')
      const skillsGroups = document.querySelectorAll('.skills-group')
      if (!section4) return
      if (section4Title) {
        gsap.set(section4Title, { opacity: 0, y: 50, scale: 0.95 })
        const titleAnim = gsap.to(section4Title, {
          scrollTrigger: {
            trigger: section4,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
          duration: 0.8,
          ease: 'power3.out',
          opacity: 1,
          y: 0,
          scale: 1,
        })
        skillsScrollTriggersRef.current.push(titleAnim.scrollTrigger)
        gsapAnimationsRef.current.push(titleAnim)
      }
      if (skillsGrid) {
        gsap.set(skillsGrid, { opacity: 0, y: 40 })
        const gridAnim = gsap.to(skillsGrid, {
          scrollTrigger: {
            trigger: section4,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
          duration: 0.9,
          ease: 'power3.out',
          opacity: 1,
          y: 0,
          delay: 0.2,
        })
        skillsScrollTriggersRef.current.push(gridAnim.scrollTrigger)
        gsapAnimationsRef.current.push(gridAnim)
      }
      if (skillsGroupsWrapper && skillsGroups.length > 0) {
        gsap.set(skillsGroups, { opacity: 0, y: 60, scale: 0.9 })
        const groupsAnim = gsap.to(skillsGroups, {
          scrollTrigger: {
            trigger: skillsGroupsWrapper,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
          duration: 0.8,
          ease: 'power3.out',
          opacity: 1,
          y: 0,
          scale: 1,
          stagger: 0.15,
        })
        skillsTriggerRef.current = groupsAnim.scrollTrigger
        skillsScrollTriggersRef.current.push(groupsAnim.scrollTrigger)
        gsapAnimationsRef.current.push(groupsAnim)
        skillsGroups.forEach((group, index) => {
          const title = group.querySelector('.skills-group-title')
          if (title) {
            gsap.set(title, { opacity: 0, y: 30 })
            const groupTitleAnim = gsap.to(title, {
              scrollTrigger: {
                trigger: group,
                start: 'top 85%',
                toggleActions: 'play none none reverse',
              },
              duration: 0.7,
              ease: 'power3.out',
              opacity: 1,
              y: 0,
              delay: index * 0.1 + 0.2,
            })
            skillsScrollTriggersRef.current.push(groupTitleAnim.scrollTrigger)
            gsapAnimationsRef.current.push(groupTitleAnim)
          }
          const tags = group.querySelectorAll('.skill-tag')
          if (tags.length > 0) {
            gsap.set(tags, { opacity: 0, y: 20, scale: 0.9 })
            const tagsAnim = gsap.to(tags, {
              scrollTrigger: {
                trigger: group,
                start: 'top 85%',
                toggleActions: 'play none none reverse',
              },
              duration: 0.6,
              ease: 'power2.out',
              opacity: 1,
              y: 0,
              scale: 1,
              stagger: 0.05,
              delay: index * 0.1 + 0.4,
            })
            skillsScrollTriggersRef.current.push(tagsAnim.scrollTrigger)
            gsapAnimationsRef.current.push(tagsAnim)
          }
        })
      }
    }, 100)
    timeoutsRef.current.push(skillsTimeout)
    const cosmicTimeout = setTimeout(() => {
      if (isCleanedUpRef.current) {
        return
      }
      const section6 = document.querySelector('.section-6')
      const section6Bg = document.querySelector('.section-6-bg')
      const section6Title = document.querySelector('[data-effect26]')
      const section6Words = document.querySelectorAll('.section-6-word')
      if (!section6 || !section6Title || !section6Bg || section6Words.length === 0) {
        return
      }
      const wordsData = []
      section6Words.forEach((word) => {
        const chars = splitTextToChars(word)
        if (chars.length > 0) {
          wordsData.push({ word, chars })
        }
      })
      if (wordsData.length === 0) return
      gsap.set(section6Bg, { opacity: 0 })
      wordsData.forEach(({ chars }, wordIndex) => {
        const origin = '50% 50%'
        const enterDirection = wordIndex % 2 === 0 ? -1 : 1
        gsap.set(chars, {
          scaleY: 0,
          scaleX: 0.8,
          opacity: 0,
          y: enterDirection * 100,
          transformOrigin: origin,
          visibility: 'visible',
          filter: 'blur(50px)',
        })
      })
      const section6Timeline = gsap.timeline({
        scrollTrigger: {
          trigger: section6,
          start: 'top top',
          end: '+=100%',
          scrub: 0.5,
          pin: true,
          anticipatePin: 1,
        }
      })
      cosmicTriggerRef.current = section6Timeline.scrollTrigger
      gsapAnimationsRef.current.push(section6Timeline)
      section6Timeline.to(
        section6Bg,
        {
          opacity: 1,
          duration: 0.15,
          ease: 'power2.out',
        },
        0
      )
      wordsData.forEach(({ chars }, wordIndex) => {
        section6Timeline.to(
          chars,
          {
            scaleY: 1,
            scaleX: 1,
            opacity: 1,
            y: 0,
            filter: 'blur(0px)',
            stagger: {
              amount: 0.25,
              from: 'edges',
            },
            ease: 'power3.out',
          },
          0.05 + wordIndex * 0.1
        )
      })
      if (!isCleanedUpRef.current) {
        ScrollTrigger.refresh()
      }
    }, 200)
    timeoutsRef.current.push(cosmicTimeout)
    const footerTimeout = setTimeout(() => {
      if (isCleanedUpRef.current) return
      const footer = document.querySelector('footer')
      const footerBgText = document.querySelector('.footer__bg-text')
      const footerTop = document.querySelector('.footer__top')
      const footerMiddle = document.querySelector('.footer__middle')
      const footerBottom = document.querySelector('.footer__bottom')
      const footerContactItems = document.querySelectorAll('.footer__contact-item')
      const footerSocials = document.querySelectorAll('.footer__social')
      if (!footer) return
      if (footerBgText) {
        gsap.set(footerBgText, { y: 100, opacity: 0 })
        const bgTextAnim = gsap.to(footerBgText, {
          scrollTrigger: {
            trigger: footer,
            start: 'top 80%',
            end: 'top 20%',
            scrub: 1,
          },
          y: 0,
          opacity: 1,
          ease: 'none',
        })
        gsapAnimationsRef.current.push(bgTextAnim)
      }
      if (footerTop) {
        gsap.set(footerTop, { y: 60, opacity: 0 })
        const topAnim = gsap.to(footerTop, {
          scrollTrigger: {
            trigger: footer,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
          y: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out',
        })
        gsapAnimationsRef.current.push(topAnim)
      }
      if (footerContactItems.length > 0) {
        gsap.set(footerContactItems, { y: 40, opacity: 0 })
        const contactAnim = gsap.to(footerContactItems, {
          scrollTrigger: {
            trigger: footerMiddle,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
        })
        gsapAnimationsRef.current.push(contactAnim)
      }
      if (footerSocials.length > 0) {
        gsap.set(footerSocials, { y: 20, opacity: 0 })
        const socialsAnim = gsap.to(footerSocials, {
          scrollTrigger: {
            trigger: footerBottom,
            start: 'top 90%',
            toggleActions: 'play none none reverse',
          },
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power2.out',
        })
        gsapAnimationsRef.current.push(socialsAnim)
      }
      if (footerBottom) {
        const copyright = footerBottom.querySelector('.footer__copyright')
        if (copyright) {
          gsap.set(copyright, { opacity: 0 })
          const copyrightAnim = gsap.to(copyright, {
            scrollTrigger: {
              trigger: footerBottom,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
            opacity: 1,
            duration: 1,
            delay: 0.5,
            ease: 'power2.out',
          })
          gsapAnimationsRef.current.push(copyrightAnim)
        }
      }
    }, 300)
    timeoutsRef.current.push(footerTimeout)
    return () => {
      cleanup()
    }
  }, [locationTagRef])
  return null
}